/**
 * HIPAA-Compliant Audit Logging Service
 * Tracks all security-relevant events for compliance and security monitoring
 */

export type AuditEventType =
  | 'USER_LOGIN'
  | 'USER_LOGOUT'
  | 'APPOINTMENT_CREATED'
  | 'APPOINTMENT_UPDATED'
  | 'APPOINTMENT_CANCELLED'
  | 'PATIENT_DATA_ACCESSED'
  | 'PATIENT_DATA_MODIFIED'
  | 'SECURITY_EVENT'
  | 'RATE_LIMIT_EXCEEDED'
  | 'VALIDATION_FAILURE'
  | 'API_ERROR'
  | 'CSRF_TOKEN_MISMATCH'
  | 'SESSION_EXPIRED'
  | 'UNAUTHORIZED_ACCESS';

export type AuditResult = 'success' | 'failure' | 'warning';

export interface AuditEvent {
  /** ISO timestamp of the event */
  timestamp: string;

  /** Type of event */
  event_type: AuditEventType;

  /** User identifier (if authenticated) */
  user_id?: string;

  /** Organization/practice identifier */
  practice_id: string;

  /** Action performed */
  action: string;

  /** Resource accessed */
  resource: string;

  /** Result of the action */
  result: AuditResult;

  /** Client IP address (logged server-side) */
  ip_address?: string;

  /** User agent string */
  user_agent: string;

  /** Session identifier */
  session_id?: string;

  /** Additional metadata */
  metadata?: Record<string, unknown>;

  /** Error details (if failure) */
  error?: {
    code: string;
    message: string;
  };
}

export interface AuditLoggerConfig {
  apiUrl: string;
  practiceId: string;
  enabled: boolean;
  debug: boolean;
  batchSize: number;
  flushInterval: number; // milliseconds
}

class AuditLogger {
  private config: AuditLoggerConfig;
  private eventQueue: AuditEvent[] = [];
  private flushTimer: NodeJS.Timeout | null = null;

  constructor(config?: Partial<AuditLoggerConfig>) {
    this.config = {
      apiUrl: import.meta.env.VITE_API_URL || 'https://api.withinehr.com',
      practiceId: 'default',
      enabled: true,
      debug: import.meta.env.DEV,
      batchSize: 10,
      flushInterval: 5000,
      ...config,
    };

    // Start flush timer
    this.startFlushTimer();
  }

  /**
   * Log an audit event
   */
  async log(event: Partial<AuditEvent>): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    const auditEvent: AuditEvent = {
      timestamp: new Date().toISOString(),
      event_type: event.event_type || 'SECURITY_EVENT',
      practice_id: event.practice_id || this.config.practiceId,
      action: event.action || 'UNKNOWN',
      resource: event.resource || 'UNKNOWN',
      result: event.result || 'success',
      user_agent: this.getUserAgent(),
      session_id: this.getSessionId(),
      metadata: this.sanitizeMetadata(event.metadata),
      ...event,
    };

    // Add to queue
    this.eventQueue.push(auditEvent);

    // Log in development
    if (this.config.debug) {
      console.log('[AUDIT]', auditEvent);
    }

    // Flush if batch size reached
    if (this.eventQueue.length >= this.config.batchSize) {
      await this.flush();
    }
  }

  /**
   * Log a security event
   */
  async logSecurityEvent(
    action: string,
    result: AuditResult,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    await this.log({
      event_type: 'SECURITY_EVENT',
      action,
      resource: 'security',
      result,
      metadata,
    });
  }

  /**
   * Log an API error
   */
  async logAPIError(
    endpoint: string,
    error: Error,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    await this.log({
      event_type: 'API_ERROR',
      action: 'API_CALL',
      resource: endpoint,
      result: 'failure',
      error: {
        code: error.name,
        message: error.message,
      },
      metadata,
    });
  }

  /**
   * Log patient data access (HIPAA requirement)
   */
  async logPatientDataAccess(
    userId: string,
    action: 'view' | 'edit' | 'create',
    resourceId: string
  ): Promise<void> {
    await this.log({
      event_type: 'PATIENT_DATA_ACCESSED',
      user_id: userId,
      action: action.toUpperCase(),
      resource: `patient_data:${resourceId}`,
      result: 'success',
    });
  }

  /**
   * Log appointment events
   */
  async logAppointmentEvent(
    action: 'created' | 'updated' | 'cancelled',
    appointmentId: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    const eventTypeMap = {
      created: 'APPOINTMENT_CREATED' as const,
      updated: 'APPOINTMENT_UPDATED' as const,
      cancelled: 'APPOINTMENT_CANCELLED' as const,
    };

    await this.log({
      event_type: eventTypeMap[action],
      action: action.toUpperCase(),
      resource: `appointment:${appointmentId}`,
      result: 'success',
      metadata,
    });
  }

  /**
   * Flush all pending events to the API
   */
  async flush(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await this.sendToAPI(events);
    } catch (error) {
      console.error('[AUDIT] Failed to send audit logs:', error);

      // Re-queue failed events (up to a limit)
      if (this.eventQueue.length < 100) {
        this.eventQueue.push(...events);
      }
    }
  }

  /**
   * Send events to the API
   */
  private async sendToAPI(events: AuditEvent[]): Promise<void> {
    try {
      const response = await fetch(`${this.config.apiUrl}/api/v1/audit/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
    } catch (error) {
      // Silent fail - don't interrupt user experience for logging failures
      if (this.config.debug) {
        console.error('[AUDIT] API error:', error);
      }
      throw error;
    }
  }

  /**
   * Get user agent string
   */
  private getUserAgent(): string {
    return navigator.userAgent;
  }

  /**
   * Get session ID from storage or generate new one
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('ww_session_id');

    if (!sessionId) {
      sessionId = this.generateSessionId();
      sessionStorage.setItem('ww_session_id', sessionId);
    }

    return sessionId;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Sanitize metadata to remove PII
   */
  private sanitizeMetadata(
    metadata?: Record<string, unknown>
  ): Record<string, unknown> | undefined {
    if (!metadata) {
      return undefined;
    }

    // Remove common PII fields
    const piiFields = [
      'password',
      'ssn',
      'social_security_number',
      'credit_card',
      'card_number',
      'cvv',
      'dateOfBirth',
      'dob',
    ];

    const sanitized = { ...metadata };

    for (const field of piiFields) {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  /**
   * Start automatic flush timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush().catch(console.error);
    }, this.config.flushInterval);
  }

  /**
   * Stop automatic flush timer
   */
  stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<AuditLoggerConfig>): void {
    this.config = { ...this.config, ...config };

    if (config.flushInterval) {
      this.startFlushTimer();
    }
  }

  /**
   * Destroy the logger and flush remaining events
   */
  async destroy(): Promise<void> {
    this.stopFlushTimer();
    await this.flush();
  }
}

// Singleton instance
let auditLoggerInstance: AuditLogger | null = null;

export function getAuditLogger(config?: Partial<AuditLoggerConfig>): AuditLogger {
  if (!auditLoggerInstance) {
    auditLoggerInstance = new AuditLogger(config);
  }
  return auditLoggerInstance;
}

export const auditLogger = getAuditLogger();
